import { z } from 'zod';
import { FISH_SPECIES_COUNT, findActiveFishSpecies } from './fish';
import { GENERATED_FISH_MANIFEST } from './fish-manifest.generated';

/**
 * **O que o runtime sabe sobre a ARTE de uma espécie** (gate V28I.BK).
 *
 * Uma projecção gerada dos masters, escrita pelo pipeline e lida por quem
 * desenha. Liga, por espécie: o id, o ficheiro autorado, o hash do master, as
 * duas dimensões do master, o caminho servido, o hash do derivado e as dimensões
 * dele.
 *
 * Vive ao lado do contrato de pesca porque é dele que fala. **Um peixe não é uma
 * `ItemDefinition`**: não tem nível, não funde, não equipa, não entra no
 * inventário e não passa por `itemArt`. O que ele partilha com um item é a
 * disciplina — a arte vem de um manifesto, e nunca de um caminho composto à mão.
 *
 * ## O que este contrato não pode carregar
 *
 * Raridade, posição, vida, peso, valor-base, preço ou probabilidade. Não há
 * campo onde coubessem, e isso é a garantia: **nada de jogo é inferido de uma
 * imagem**. O que uma espécie É vive em `fish.ts` e em `fish-price.ts`, decidido
 * por pessoas.
 *
 * ## As vinte retiradas não estão aqui, e não é um buraco
 *
 * O gate V28I.BK trocou as vinte espécies provisórias pelas vinte e sete
 * definitivas. As vinte antigas continuam a existir no registo — uma captura em
 * `account_catches` vale hoje o que valia — mas nunca tiveram arte e não vão
 * passar a ter. {@link fishArt} responde-lhes `null`, e quem desenha mostra o
 * recuo de "sem imagem" com o nome certo por baixo.
 */

export const FISH_MANIFEST_VERSION = 1;

export const fishManifestEntrySchema = z
  .object({
    /** O id canónico da espécie. É também o nome do ficheiro master. */
    speciesId: z.string().min(1),
    /** O ficheiro autorado, sob `assets-source/fish/`. */
    master: z.string().min(1),
    masterSha256: z.string().regex(/^[0-9a-f]{64}$/),
    masterWidth: z.number().int().positive(),
    masterHeight: z.number().int().positive(),
    /** O caminho servido, relativo à raiz de assets. */
    file: z.string().min(1),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  })
  .strict();

export type FishManifestEntry = z.infer<typeof fishManifestEntrySchema>;

export const fishManifestSchema = z
  .object({
    manifestVersion: z.number().int().min(1),
    entries: z.array(fishManifestEntrySchema),
  })
  .strict();

export type FishManifest = z.infer<typeof fishManifestSchema>;

/** O manifesto embutido, tal como o pipeline o escreveu. */
export const FISH_MANIFEST: FishManifest = GENERATED_FISH_MANIFEST;

const BY_ID: ReadonlyMap<string, FishManifestEntry> = new Map(
  FISH_MANIFEST.entries.map((entry) => [entry.speciesId, entry]),
);

/**
 * **A arte desta espécie, ou `null` quando ela não tem.**
 *
 * ## O contrato de RECUO, e o que ele proíbe
 *
 * `null` não é um erro: é a resposta para as vinte espécies retiradas, e para
 * qualquer id que este build não conheça. Quem desenha trata-o como o inventário
 * já trata uma definição sem arte — o recuo genérico de item sem imagem.
 *
 * Três coisas que uma superfície **nunca** pode fazer com este `null`:
 *
 * - **esconder o exemplar.** Um peixe sem arte continua a ser um peixe que o
 *   jogador pescou, que ocupa carga e que vale Ouro no comerciante. Escondê-lo
 *   faria a carga mentir sobre o que a nave transporta;
 * - **partir.** Nem a caixa aberta, nem a lista do cofre, nem o recibo de venda
 *   podem lançar, ficar em branco ou desaparecer por falta de uma imagem;
 * - **inventar um caminho.** Compor `fish/${id}.png` à mão produziria um pedido
 *   para um ficheiro que não está lá. O caminho vem daqui ou não vem de lado
 *   nenhum.
 *
 * E uma que ela **tem sempre** de fazer: mostrar o NOME. Um exemplar sem arte
 * ainda se chama alguma coisa, e o nome vem do catálogo — nunca o id em bruto.
 */
export function fishArt(speciesId: string): FishManifestEntry | null {
  return BY_ID.get(speciesId) ?? null;
}

/** Se o pipeline já emitiu arte para esta espécie. */
export function hasFishArt(speciesId: string): boolean {
  return BY_ID.has(speciesId);
}

/**
 * Se o manifesto descreve exactamente as espécies activas: nem a mais, nem a
 * menos.
 *
 * `true` também para o manifesto VAZIO — "nenhuma arte emitida" é coerente com o
 * catálogo, e é o estado de um repositório acabado de clonar, onde
 * `game-assets/` ainda não existe. O que isto recusa é uma entrada para um id
 * que não é uma espécie activa, um id repetido, e um conjunto parcialmente
 * emitido.
 */
export function fishManifestMatchesCatalogue(manifest: FishManifest = FISH_MANIFEST): boolean {
  if (manifest.entries.length === 0) {
    return true;
  }
  const ids = manifest.entries.map((entry) => entry.speciesId);
  if (new Set(ids).size !== ids.length) {
    return false;
  }
  /*
   * ACTIVA, e não apenas conhecida. `isFishSpeciesId` responde `true` a uma das
   * vinte retiradas, e uma entrada de manifesto para uma delas seria arte que
   * nada sorteia — exactamente o que este predicado tem de recusar.
   */
  if (!ids.every((id) => findActiveFishSpecies(id) !== null)) {
    return false;
  }
  return ids.length === FISH_SPECIES_COUNT;
}
